import type { Store } from "@redotech/util/store";
import { epochSecondsToInstant } from "@redotech/util/temporal";
import { localStore, storageKeys } from "@redotech/web-util/storage";
import jwtDecode from "jwt-decode";

export interface Auth {
  expiration: Temporal.Instant;
  handoffSource?: OnboardingAuthHandoffSource;
  /**
   * JWT `iat` claim. Used to pick the most recently issued token when the
   * onboarding app falls back to localStorage and finds tokens for multiple
   * teams (a common state when a single user has tested onboarding against
   * multiple Shopify stores). Defaults to epoch 0 when the claim is missing.
   */
  issuedAt: Temporal.Instant;
  teamId: string;
  userId: string;
  token: string;
}

export type OnboardingAuthHandoffSource = "stored-token-resume" | "url-token";

export const TOKEN_KEY = "redo.merchant_auth_token";

export function teamAuthStore(teamId: string): Store<string | undefined> {
  return localStore(`${TOKEN_KEY}.${teamId}`);
}

export function parseAuth(token: string): Auth | undefined {
  try {
    const decoded = jwtDecode<any>(token);
    const teamId = decoded.team_id ?? decoded.aud.split("/")[1];
    const userId = decoded.id ?? decoded.sub;
    if (
      !teamId ||
      typeof teamId !== "string" ||
      !userId ||
      typeof userId !== "string"
    ) {
      throw new TypeError("Not a valid Auth object");
    }
    const issuedAtEpochSeconds =
      typeof decoded.iat === "number" && Number.isFinite(decoded.iat)
        ? decoded.iat
        : 0;
    return {
      expiration: epochSecondsToInstant(decoded.exp),
      issuedAt: epochSecondsToInstant(issuedAtEpochSeconds),
      teamId,
      token,
      userId,
    };
  } catch (error) {
    console.error("Failed to parse token", error);
    return undefined;
  }
}

/** @returns whether the auth is expired (or expires within the next second) */
export function isExpired(expiration: Temporal.Instant): boolean {
  const duration = expiration.since(Temporal.Now.instant());
  return (
    Temporal.Duration.compare(
      Temporal.Duration.from({ seconds: 1 }),
      duration,
    ) > 0
  );
}

export function cleanupExpiredTokens(): void {
  for (const key of storageKeys(localStorage)) {
    if (!key.startsWith(`${TOKEN_KEY}.`)) {
      continue;
    }
    const store = localStore(key);
    const token = store.get();
    if (!token) {
      continue;
    }
    const parsed = parseAuth(token);
    if (parsed && !isExpired(parsed.expiration)) {
      continue;
    }
    store.set(undefined);
  }
}

/**
 * Read token from URL search params (if present) or fall back to localStorage.
 * Persists a new URL token to the per-team store.
 *
 * When falling back to localStorage and multiple non-expired tokens exist
 * (e.g. a developer or Redo employee who has tested onboarding against more
 * than one Shopify store), pick the token with the most recent `iat` claim.
 * That reliably resolves to whichever team the user most recently started
 * an onboarding session for — the merchant-server mints a fresh token on
 * each session start, so iat ordering matches session recency.
 */
export function initAuthFromUrl(): Auth | undefined {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get("token");

  if (urlToken) {
    const auth = parseAuth(urlToken);
    if (auth && !isExpired(auth.expiration)) {
      teamAuthStore(auth.teamId).set(urlToken);
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("token");
      cleanUrl.searchParams.delete("teamId");
      window.history.replaceState({}, "", cleanUrl.toString());
      return { ...auth, handoffSource: "url-token" };
    }
  }

  const candidates: Auth[] = [];
  for (const key of storageKeys(localStorage)) {
    if (!key.startsWith(`${TOKEN_KEY}.`)) {
      continue;
    }
    const store = localStore(key);
    const token = store.get();
    if (!token) {
      continue;
    }
    const auth = parseAuth(token);
    if (auth && !isExpired(auth.expiration)) {
      candidates.push({ ...auth, handoffSource: "stored-token-resume" });
    }
  }

  if (candidates.length === 0) {
    return undefined;
  }

  // Return the token with the latest issuedAt — the most recently minted
  // session is the one the user is most likely to be working with.
  return candidates.reduce((latest, candidate) =>
    Temporal.Instant.compare(candidate.issuedAt, latest.issuedAt) > 0
      ? candidate
      : latest,
  );
}

declare const process: { env: Record<string, string | undefined> };

export function getMerchantServerUrl(): string {
  return process.env.REDO_MERCHANT_SERVER_URL ?? "http://localhost:8007";
}

export function getDashboardUrl(
  token: string | undefined,
  nextPath = "/home/overview",
): string {
  let baseUrl = "https://app.getredo.com";
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost") {
      baseUrl = "http://localhost:13003";
    } else if (hostname.endsWith(".getredo.localhost")) {
      baseUrl = "https://app-vite.getredo.localhost";
    }
  }

  if (!token) {
    return baseUrl;
  }

  return `${baseUrl}/login/token?token=${encodeURIComponent(token)}&next=${encodeURIComponent(nextPath)}`;
}
